import Quill, { EmitterSource } from 'quill';
import { Delta } from 'quill/core';
import { CSSProperties, useEffect, useRef, useState } from 'react';

export interface Props {
  theme?: string;
  placeholder?: string;
  className?: string;
  defaultValue?: any;
  value?: any;
  readOnly?: boolean;
  modules?: any;
  style?: CSSProperties;
  onChange?: (
    value: string,
    delta: Delta,
    source: EmitterSource,
    quill: Quill,
  ) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onQuillReady?: (quill: Quill) => void;
}

export default function ReactQuill({
  defaultValue,
  value,
  theme = 'bubble',
  placeholder,
  className,
  readOnly,
  modules = {},
  style,
  onChange,
  onFocus,
  onBlur,
  onQuillReady,
}: Props) {
  const quillContainerRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const [quill, setQuill] = useState<Quill | null>(null);

  useEffect(() => {
    if (!quillContainerRef.current) return;
    if (!quillRef.current) {
      // Disable any toolbar from the module config
      modules.toolbar = false;
      const quill = new Quill(quillContainerRef.current, {
        theme: theme,
        placeholder: placeholder,
        readOnly: readOnly,
        modules: modules,
        bounds: quillContainerRef.current,
      });

      if (defaultValue) {
        if (typeof defaultValue === 'string') {
          quill.setText(defaultValue, 'silent');
        } else {
          quill.setContents(defaultValue || [], 'silent');
        }
      }

      quillRef.current = quill;
      setQuill(quill);
      if (onQuillReady) {
        onQuillReady(quill);
      }
    }

    const quillInstance = quillRef.current;

    const handleTextChange = (
      delta: Delta,
      _: Delta,
      source: EmitterSource,
    ) => {
      onChange?.(quillInstance.root.innerHTML, delta, source, quillInstance);
    };
    quillInstance.on('text-change', handleTextChange);

    const handleSelectionChange = (
      range: Range | null,
      oldRange: Range | null,
      _: EmitterSource,
    ) => {
      if (range && !oldRange) {
        onFocus?.();
      } else if (!range && oldRange) {
        onBlur?.();
      }
    };
    quillInstance.on('selection-change', handleSelectionChange);

    return () => {
      quillInstance.off('text-change', handleTextChange);
      quillInstance.off('selection-change', handleSelectionChange);
    };
  }, [
    modules,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    readOnly,
    theme,
    defaultValue,
    onQuillReady,
  ]);

  useEffect(() => {
    if (!quill || value === quill.root.innerHTML) return;
    // Only update if the value is different from the current content
    if (
      !value ||
      (typeof value === 'object' &&
        'diff' in value &&
        value.diff(quill.getContents()).changeLength() === 0)
    ) {
      return;
    }

    if (!value || typeof value === 'string') {
      quill.setText(value, 'silent');
    } else {
      quill.setContents(value || [], 'silent');
    }
  }, [value, quill]);

  return (
    <div className={className} style={style}>
      <div ref={quillContainerRef} />
    </div>
  );
}
